/**
 * OrejaVoiceTextInput - Shared voice/text toggle input used by recording and clarification phases.
 *
 * Provides a tabbed interface to switch between voice recording (via useAudioRecorder)
 * and text input (auto-growing textarea). Persists the user's input mode preference
 * to localStorage.
 *
 * @module OrejaVoiceTextInput
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { Mic, Square, AlertTriangle, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OrejaInputMode } from '@/lib/oreja/types';
import { AudioWaveform } from './AudioWaveform';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'oreja_input_mode';
const WARN_AT_SEC = 150;
/** Minimum recording duration in seconds to accept (filters accidental taps). */
const MIN_RECORDING_SEC = 1;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Input payload produced by this component. */
export type OrejaInputPayload =
  | { type: 'voice'; blob: Blob }
  | { type: 'text'; text: string };

export interface OrejaVoiceTextInputProps {
  /** Callback when user has a complete input ready (blob or text). */
  onInput: (payload: OrejaInputPayload) => void;
  /** Callback for text submission (separate from onInput for auto-send voice flow). */
  onTextSubmit?: (text: string) => void;
  /** Placeholder text for the textarea. */
  placeholder?: string;
  /** Whether the parent is processing (disables controls). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats seconds into mm:ss display.
 *
 * @param seconds - Duration in seconds
 * @returns Formatted string like "02:35"
 */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Reads the saved input mode from localStorage.
 *
 * @returns The persisted input mode, defaulting to 'voice'
 */
function getSavedMode(): OrejaInputMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'text' || saved === 'voice') return saved;
  } catch {
    // localStorage may be unavailable
  }
  return 'voice';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Tabbed voice/text input used across Oreja phases.
 *
 * @param props - Component props
 * @returns Voice/text toggle with recording controls or textarea
 */
export function OrejaVoiceTextInput({
  onInput,
  onTextSubmit,
  placeholder = 'Enter your task update details...',
  disabled = false,
}: OrejaVoiceTextInputProps) {
  const [mode, setMode] = useState<OrejaInputMode>(getSavedMode);
  const [text, setText] = useState('');

  const recorder = useAudioRecorder({ warnAtSec: WARN_AT_SEC });

  // Track the last recording duration so it survives the stopped->reset transition
  const lastDurationRef = useRef(0);
  useEffect(() => {
    if (recorder.status === 'recording') {
      lastDurationRef.current = recorder.duration;
    }
  }, [recorder.status, recorder.duration]);

  // Persist mode preference
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore
    }
  }, [mode]);

  // When recording stops with a blob, deliver it (if long enough)
  useEffect(() => {
    if (recorder.status === 'stopped' && recorder.audioBlob) {
      if (lastDurationRef.current < MIN_RECORDING_SEC) {
        toast.info('Recording too short \u2014 try again');
        recorder.reset();
        return;
      }
      onInput({ type: 'voice', blob: recorder.audioBlob });
      recorder.reset();
    }
  }, [recorder.status, recorder.audioBlob, onInput, recorder]);

  const handleSendText = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (onTextSubmit) {
      onTextSubmit(trimmed);
    } else {
      onInput({ type: 'text', text: trimmed });
    }
    setText('');
  }, [text, onInput, onTextSubmit]);

  const isRecording = recorder.status === 'recording';

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex rounded-md border overflow-hidden">
        <button
          type="button"
          className={cn(
            'flex-1 px-3 py-1.5 text-sm font-medium transition-colors',
            mode === 'voice'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted',
          )}
          onClick={() => setMode('voice')}
          disabled={disabled || isRecording}
        >
          Voice
        </button>
        <button
          type="button"
          className={cn(
            'flex-1 px-3 py-1.5 text-sm font-medium transition-colors',
            mode === 'text'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted',
          )}
          onClick={() => setMode('text')}
          disabled={disabled || isRecording}
        >
          Text
        </button>
      </div>

      {/* Voice mode */}
      {mode === 'voice' && (
        <div className="flex flex-col items-center gap-3 py-4">
          {recorder.error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 w-full">
              <p className="font-medium">{recorder.error}</p>
            </div>
          )}

          {/* Mic / Stop button with audio level ring */}
          <div className="relative flex items-center justify-center">
            {isRecording && (
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-destructive/20 transition-all duration-75"
                style={{
                  width: `${48 + recorder.audioLevel * 32}px`,
                  height: `${48 + recorder.audioLevel * 32}px`,
                }}
              />
            )}
            <button
              type="button"
              onClick={isRecording ? recorder.stopRecording : recorder.startRecording}
              disabled={disabled || recorder.status === 'requesting'}
              className={cn(
                'relative h-12 w-12 rounded-full flex items-center justify-center transition-all',
                isRecording
                  ? 'bg-destructive text-destructive-foreground'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90',
                (disabled || recorder.status === 'requesting') && 'opacity-50 cursor-not-allowed',
              )}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}
            >
              {isRecording ? (
                <Square className="h-5 w-5" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Waveform + duration counter */}
          {isRecording && (
            <div className="flex items-center gap-3">
              <AudioWaveform
                stream={recorder.stream}
                width={120}
                height={32}
                barWidth={3}
                gap={2}
                barColor="hsl(0, 84%, 60%)"
                fftSize={256}
                smoothingTimeConstant={0.6}
              />
              <span className="text-lg font-mono tabular-nums">
                {formatDuration(recorder.duration)}
              </span>
            </div>
          )}

          {/* Warning banner */}
          {recorder.showWarning && (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-md px-3 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Quick updates work best under 3 minutes — approaching limit
            </div>
          )}

          {!isRecording && !recorder.error && (
            <p className="text-xs text-muted-foreground">
              Tap the mic to start recording
            </p>
          )}
        </div>
      )}

      {/* Text mode */}
      {mode === 'text' && (
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="min-h-[80px] resize-y"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSendText();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {text.trim() ? `${text.trim().length} chars` : 'Ctrl+Enter to send'}
            </span>
            <Button
              onClick={handleSendText}
              disabled={disabled || !text.trim()}
              className="w-auto"
            >
              <Send className="mr-2 h-4 w-4" />
              Send Update
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
